import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpenIcon, Bars3Icon, XMarkIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import Navbar from '../layout/NavBar';
import NavbarNoLogin from '../layout/NavBarNoLogin';

const TableOfContents = ({ headings, activeId }) => {
  const getLevel = (heading) => {
    return parseInt(heading.tagName[1]);
  };

  return (
    <nav className="space-y-1">
      {headings.map((heading) => {
        const level = getLevel(heading);
        const isActive = heading.id === activeId;
        
        return (
          <a
            key={heading.id}
            href={`#${heading.id}`}
            className={`
              block text-sm py-1.5 transition-colors duration-200
              ${level === 1 ? 'font-semibold' : ''}
              ${level === 2 ? 'pl-4' : ''}
              ${level === 3 ? 'pl-8' : ''}
              ${level === 4 ? 'pl-12' : ''}
              ${isActive 
                ? 'text-primary font-medium bg-primary/5 rounded-lg pl-6' 
                : 'text-base-content/70 hover:text-base-content hover:bg-base-200/50 rounded-lg pl-6'}
            `}
            onClick={(e) => {
              e.preventDefault();
              const element = document.getElementById(heading.id);
              if (element) {
                const navbarHeight = 64;
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - navbarHeight - 20;

                window.scrollTo({
                  top: offsetPosition,
                  behavior: "smooth"
                });
              }
            }}
          >
            {heading.textContent}
          </a>
        );
      })}
    </nav>
  );
};

const WikiLayout = ({ children }) => {
  const { isAuthenticated } = useContext(AuthContext);
  const [headings, setHeadings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();

  // Get current article name from path
  const getCurrentArticle = () => {
    const path = location.pathname;
    if (path === '/wiki') return null;
    
    const articleName = path.split('/').pop();
    return articleName
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Extract headings from content
  useEffect(() => {
    const elements = Array.from(document.querySelectorAll('h2, h3, h4'));
    setHeadings(elements);
  }, [children]);

  // Track active section with better accuracy
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the first visible heading
        const visibleHeading = entries.find(entry => entry.isIntersecting);
        if (visibleHeading) {
          setActiveId(visibleHeading.target.id);
        } else {
          // If no heading is visible, find the last heading that's above the viewport
          const aboveViewport = entries
            .filter(entry => entry.boundingClientRect.top < 0)
            .sort((a, b) => b.boundingClientRect.top - a.boundingClientRect.top);
          
          if (aboveViewport.length > 0) {
            setActiveId(aboveViewport[0].target.id);
          }
        }
      },
      {
        rootMargin: '-80px 0px -40% 0px',
        threshold: [0, 1]
      }
    );

    headings.forEach((heading) => observer.observe(heading));
    return () => observer.disconnect();
  }, [headings]);

  return (
    <div className="min-h-screen">
      {/* Fixed Navigation Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-base-100 border-b border-base-200">
        {isAuthenticated ? <Navbar /> : <NavbarNoLogin />}
      </div>

      {/* Sidebar */}
      <aside
        className={`
          fixed left-0 top-16 bottom-0 z-40 w-72
          bg-base-100 border-r border-base-200
          transition-transform duration-300 ease-in-out
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Sidebar Content */}
        <div className="h-full flex flex-col">
          {/* Fixed Header Section */}
          <div className="flex-none p-4 border-b border-base-200">
            <div className="flex flex-col space-y-2">
              {/* Breadcrumb Navigation */}
              <div className="flex items-center text-sm">
                <Link
                  to="/wiki"
                  className="flex items-center text-base-content/70 hover:text-primary transition-colors duration-200"
                >
                  <BookOpenIcon className="h-4 w-4 mr-1" />
                  Wiki
                </Link>
                
                {getCurrentArticle() && (
                  <>
                    <ChevronRightIcon className="h-4 w-4 mx-1 text-base-content/40" />
                    <span className="text-primary font-medium truncate">
                      {getCurrentArticle()}
                    </span>
                  </>
                )}
              </div>

              {/* Table of Contents Header */}
              <div className="font-semibold uppercase tracking-wider text-base-content/50 mt-2">
                Inhaltsverzeichnis
              </div>
            </div>
          </div>

          {/* Scrollable Navigation Section */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-4">
              <TableOfContents headings={headings} activeId={activeId} />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile Toggle Button */}
      <button
        type="button"
        className="fixed bottom-4 right-4 z-50 lg:hidden rounded-full bg-primary p-3 text-white shadow-lg"
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
      >
        {isSidebarOpen ? (
          <XMarkIcon className="h-6 w-6" />
        ) : (
          <Bars3Icon className="h-6 w-6" />
        )}
      </button>

      {/* Main Content Area */}
      <main className="lg:pl-72 pt-16">
        <div className="max-w-5xl mx-auto px-4 lg:px-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default WikiLayout; 